<script lang="ts">
	import type { EmblaCarouselType } from "embla-carousel";
	import type { Category } from "@core/schema/Category.js";
	import type { Media as MediaType } from "@core/schema/Media.js";
	import CarouselArrows from "./CarouselArrows.svelte";
	import Carousel from "./common/Carousel.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Media from "./common/Media.svelte";

	const {
		gallery,
		bgColor = "",
	}: {
		gallery: Category["gallery"];
		bgColor?: string;
	} = $props();

	let carouselApi: EmblaCarouselType | undefined = $state();
</script>

{#if gallery?.photos}
	<section class="full-width grid-container {bgColor} py-8 sm:py-12 md:py-16 lg:py-20">
		<div class="container-center">
			<div class="flex flex-col items-center gap-4 sm:gap-6 mb-10 md:mb-14">
				{#if gallery.title}
					<h2 class="text-center">
						<Formatted text={gallery.title} />
					</h2>
				{/if}
				{#if gallery.description}
					<p class="font-normal text-center lg:w-[65%] mx-auto text-base lg:text-lg/[1.5] text-optical-black/80">
						<Formatted text={gallery.description} />
					</p>
				{/if}
			</div>
		</div>

		<div class="col-span-full flex flex-col gap-8">
			<Carousel
				items={gallery.photos}
				options={{
					loop: true,
				}}
				bind:api={carouselApi}
			>
				{#snippet item(photo: MediaType, index: number)}
					{@const originalIndex = index % gallery.photos.length}
					<!-- svelte-ignore a11y_no_static_element_interactions -->
					<!-- svelte-ignore a11y_click_events_have_key_events -->
					<div
						class="basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 shrink-0 grow-0 mr-4 group cursor-pointer outline-none"
						onclick={(e: MouseEvent): void => {
							const link = (e.currentTarget as HTMLElement).querySelector<HTMLElement>("a[data-fslightbox]");
							link?.click();
						}}
					>
						<div
							class="relative w-full h-64 sm:h-72 md:h-80 lg:h-[25rem] rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow duration-300 bg-gray-100"
						>
							<Media
								class="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
								source={photo}
								wrapperClass="absolute inset-0"
								width={600}
								height={400}
								alt={photo.title ?? `Gallery ${originalIndex + 1}`}
								lightbox="gallery"
								lightboxWidth={1400}
							/>
							<div
								class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"
							></div>
							<div class="hidden sm:block absolute bottom-0 left-0 right-0 p-4 sm:p-5 lg:p-6 pointer-events-none">
								<h3 class="text-white text-sm sm:text-base font-semibold drop-shadow-2xl leading-tight">
									<Formatted text={photo.title} />
								</h3>
							</div>
						</div>
						<p class="sm:hidden mt-3 text-sm text-optical-black font-medium leading-snug px-1">
							<Formatted text={photo.title} />
						</p>
					</div>
				{/snippet}
			</Carousel>

			<div class="sm:flex hidden flex-col items-center">
				<div class="container-center">
					<CarouselArrows api={carouselApi} slideCount={gallery.photos.length} />
				</div>
			</div>
		</div>
	</section>
{/if}
